import gsap from 'gsap';
import { Container, Graphics } from 'pixi.js';
import { createOlympusBackdrop } from '../rendering/olympusBackdrop.js';
import { gameConfig } from '../config/gameConfig.js';

/**
 * Slow ambient motion: backdrop wisps, sand motes, logo/glow breathing, spin idle ring.
 */
export class AmbientIdleFx {
  /**
   * @param {{
   *   backdropRoot: import('pixi.js').Container,
   *   glowSprite?: import('pixi.js').Sprite | null,
   *   logoSprite?: import('pixi.js').Sprite | null,
   *   spinButtonRoot?: import('pixi.js').Container | null,
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   *   isSpinIdle?: () => boolean,
   * }} opts
   */
  constructor(opts) {
    this._backdropRoot = opts.backdropRoot;
    this._glowSprite = opts.glowSprite ?? null;
    this._logoSprite = opts.logoSprite ?? null;
    this._spinButtonRoot = opts.spinButtonRoot ?? null;
    this._animationController = opts.animationController;
    this._isSpinIdle = opts.isSpinIdle ?? (() => true);

    this._ambientLayer = new Container();
    this._ambientLayer.eventMode = 'none';
    this._backdropRoot.addChild(this._ambientLayer);

    /** @type {import('gsap').Tween | null} */
    this._wispsTween = null;
    /** @type {import('gsap').Tween | null} */
    this._glowIdleTween = null;
    /** @type {import('gsap').Tween | null} */
    this._logoIdleTween = null;
    /** @type {Graphics | null} */
    this._spinRing = null;
    /** @type {import('gsap').Tween | null} */
    this._spinRingTween = null;
    /** @type {import('gsap').Tween | null} */
    this._spinRingAlphaTween = null;
    /** @type {ReturnType<typeof setInterval> | null} */
    this._moteTimer = null;
    this._motesPaused = false;
    this._started = false;
  }

  start() {
    if (this._started) return;
    this._started = true;

    const theme = gameConfig.theme ?? {};
    const wisps = createOlympusBackdrop(theme);
    wisps.alpha = 0.85;
    this._ambientLayer.addChild(wisps);

    this._wispsTween = gsap.to(wisps, {
      x: 12,
      duration: 28,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
    this._animationController.trackTween(this._wispsTween);

    this._startGlowIdle();
    this._startLogoIdle();
    this._attachSpinRing();
    this._startSandMotes();
  }

  setGlowSprite(sprite) {
    this._glowSprite = sprite;
    this._startGlowIdle();
  }

  setLogoSprite(sprite) {
    if (this._logoIdleTween) {
      this._logoIdleTween.kill();
      this._animationController.releaseTween(this._logoIdleTween);
      this._logoIdleTween = null;
    }
    this._logoSprite = sprite;
    this._startLogoIdle();
  }

  setSpinButtonRoot(root) {
    this._spinButtonRoot = root;
    this._attachSpinRing();
  }

  _startGlowIdle() {
    if (!this._glowSprite) return;
    if (this._glowIdleTween) {
      this._glowIdleTween.kill();
      this._animationController.releaseTween(this._glowIdleTween);
    }
    const fx = gameConfig.visualFx ?? {};
    const base = this._glowSprite.alpha;
    this._glowIdleTween = gsap.to(this._glowSprite, {
      alpha: Math.min(1, base + (fx.idleGlowPulseSwing ?? 0.12)),
      duration: fx.idleGlowPulseSec ?? 3.2,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
    this._animationController.trackTween(this._glowIdleTween);
  }

  _startLogoIdle() {
    if (!this._logoSprite) return;
    if (this._logoIdleTween) {
      this._logoIdleTween.kill();
      this._animationController.releaseTween(this._logoIdleTween);
    }
    const fx = gameConfig.visualFx ?? {};
    const baseY = this._logoSprite.y;
    this._logoIdleTween = gsap.to(this._logoSprite, {
      y: baseY - (fx.idleLogoFloatPx ?? 4),
      duration: fx.idleLogoFloatSec ?? 2.8,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
    this._animationController.trackTween(this._logoIdleTween);
  }

  _attachSpinRing() {
    const root = this._spinButtonRoot;
    if (!root || this._spinRing?.parent === root) return;

    if (this._spinRing) {
      this._spinRing.parent?.removeChild(this._spinRing);
      this._spinRing.destroy();
      this._spinRing = null;
    }
    if (this._spinRingTween) {
      this._spinRingTween.kill();
      this._animationController.releaseTween(this._spinRingTween);
      this._spinRingTween = null;
    }
    if (this._spinRingAlphaTween) {
      this._spinRingAlphaTween.kill();
      this._animationController.releaseTween(this._spinRingAlphaTween);
      this._spinRingAlphaTween = null;
    }

    const fx = gameConfig.visualFx ?? {};
    const ring = new Graphics();
    const r = fx.spinIdleRingRadius ?? 78;
    ring.circle(0, 0, r).stroke({
      width: 2.5,
      color: gameConfig.theme?.gold ?? 0xe7c463,
      alpha: 0.45,
    });
    ring.circle(0, 0, r - 6).stroke({
      width: 1,
      color: gameConfig.theme?.goldBright ?? 0xfff8dc,
      alpha: 0.2,
    });
    ring.eventMode = 'none';
    ring.visible = false;
    root.addChildAt(ring, 0);
    this._spinRing = ring;

    this._spinRingTween = gsap.to(ring, {
      rotation: Math.PI * 2,
      duration: fx.spinIdleRingRotateSec ?? 8,
      repeat: -1,
      ease: 'none',
      onStart: () => {
        ring.visible = this._isSpinIdle();
      },
    });
    this._animationController.trackTween(this._spinRingTween);

    this._spinRingAlphaTween = gsap.to(ring, {
      alpha: 0.35,
      duration: 1.4,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
    this._animationController.trackTween(this._spinRingAlphaTween);
  }

  _setSandMotesPaused(paused) {
    if (this._motesPaused === paused) return;
    this._motesPaused = paused;
    if (paused && this._moteTimer) {
      clearInterval(this._moteTimer);
      this._moteTimer = null;
      this._clearSandMotes();
      return;
    }
    if (!paused && this._started) {
      this._startSandMotes();
    }
  }

  /** Remove in-flight mote Graphics only (keep backdrop wisps). */
  _clearSandMotes() {
    if (!this._ambientLayer || this._ambientLayer.destroyed) return;
    const kids = this._ambientLayer.children.slice();
    for (let i = 0; i < kids.length; i++) {
      const child = kids[i];
      if (!(child instanceof Graphics)) continue;
      gsap.killTweensOf(child);
      this._ambientLayer.removeChild(child);
      try {
        child.destroy();
      } catch (_e) {
        /* ignore */
      }
    }
  }

  syncSpinRingVisible(spinning) {
    this._setSandMotesPaused(spinning);
    if (this._spinRing) {
      this._spinRing.visible = !spinning && this._isSpinIdle();
    }
  }

  _startSandMotes() {
    if (this._moteTimer || this._motesPaused) return;
    const fx = gameConfig.visualFx ?? {};
    const interval = fx.ambientMoteIntervalMs ?? 420;
    const maxMotes = Math.max(4, Number(fx.ambientMoteMaxConcurrent) || 12);
    this._moteTimer = setInterval(() => {
      if (this._motesPaused) return;
      if (Math.random() > (fx.ambientMoteChance ?? 0.55)) return;
      let moteCount = 0;
      for (let i = 0; i < this._ambientLayer.children.length; i++) {
        if (this._ambientLayer.children[i] instanceof Graphics) moteCount += 1;
      }
      if (moteCount >= maxMotes) return;
      const mote = new Graphics();
      const s = 1.5 + Math.random() * 2.5;
      mote.circle(0, 0, s).fill({
        color: gameConfig.theme?.cloudTint ?? 0x8b7355,
        alpha: 0.35 + Math.random() * 0.25,
      });
      mote.x = -800 + Math.random() * 1600;
      mote.y = 200 + Math.random() * 500;
      mote.eventMode = 'none';
      this._ambientLayer.addChild(mote);

      gsap.to(mote, {
        x: mote.x + 40 + Math.random() * 80,
        y: mote.y - 60 - Math.random() * 80,
        alpha: 0,
        duration: 4 + Math.random() * 3,
        ease: 'sine.out',
        onComplete: () => {
          if (mote.destroyed) return;
          mote.parent?.removeChild(mote);
          mote.destroy();
        },
      });
    }, interval);
  }

  destroy() {
    if (this._moteTimer) clearInterval(this._moteTimer);
    this._moteTimer = null;
    this._clearSandMotes();
    if (this._wispsTween) {
      this._wispsTween.kill();
      this._animationController.releaseTween(this._wispsTween);
      this._wispsTween = null;
    }
    if (this._glowIdleTween) {
      this._glowIdleTween.kill();
      this._animationController.releaseTween(this._glowIdleTween);
      this._glowIdleTween = null;
    }
    if (this._logoIdleTween) {
      this._logoIdleTween.kill();
      this._animationController.releaseTween(this._logoIdleTween);
      this._logoIdleTween = null;
    }
    if (this._spinRingTween) {
      this._spinRingTween.kill();
      this._animationController.releaseTween(this._spinRingTween);
      this._spinRingTween = null;
    }
    if (this._spinRingAlphaTween) {
      this._spinRingAlphaTween.kill();
      this._animationController.releaseTween(this._spinRingAlphaTween);
      this._spinRingAlphaTween = null;
    }
    this._ambientLayer.parent?.removeChild(this._ambientLayer);
    this._ambientLayer.destroy({ children: true });
    this._spinRing = null;
    this._started = false;
  }
}
